import { mergeProps } from '@base-ui/react/merge-props';
import { useRender } from '@base-ui/react/use-render';
import { cva, type VariantProps } from 'class-variance-authority';
import type { ComponentProps } from 'react';

import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/Tooltip';
import { cn } from '@/lib/utils';

import useSidebar from './useSidebar';

export type SidebarMenuButtonTooltip = string | ComponentProps<typeof TooltipContent>;

export type SidebarMenuButtonProps = useRender.ComponentProps<'button'> &
  ComponentProps<'button'> & {
    isActive?: boolean;
    tooltip?: SidebarMenuButtonTooltip;
  } & VariantProps<typeof sidebarMenuButtonVariants>;

const sidebarMenuButtonVariants = cva(
  [
    'peer/menu-button group/menu-button',
    'flex w-full items-center overflow-hidden',
    'cursor-pointer gap-2 rounded-none px-2 py-5 text-left text-sm',
    'ring-white outline-hidden',
    'transition-[width,height,padding]',
    '*:data-[slot=sidebar-expand-icon]:invisible hover:*:data-[slot=sidebar-expand-icon]:visible focus:*:data-[slot=sidebar-expand-icon]:visible',
    'aria-expanded:bg-gray-200 aria-expanded:*:data-[slot=sidebar-expand-icon]:visible',
    '[&_svg]:size-4.5 [&_svg]:shrink-0 [&>span:last-child]:truncate',

    'focus:bg-gray-200 focus:text-gray-900',
    'hover:bg-gray-200 hover:text-gray-900',
    'active:bg-gray-300 active:text-gray-900',
    'data-active:bg-primary! data-active:font-semibold data-active:text-gray-900',
    'data-open:hover:bg-gray-200 data-open:hover:text-gray-900',

    'disabled:pointer-events-none disabled:opacity-50 aria-disabled:pointer-events-none aria-disabled:opacity-50 ',

    'group-has-data-[sidebar=menu-action]/menu-item:pr-8 group-data-[collapsible=icon]:size-8! group-data-[collapsible=icon]:p-2! ',
  ],
  {
    variants: {
      variant: {
        default: 'hover:bg-gray-200 hover:text-gray-900',
        outline:
          'bg-white shadow-[0_0_0_1px_hsl(var(--sidebar-border))] hover:bg-gray-200 hover:text-gray-900 hover:shadow-[0_0_0_1px_hsl(var(--sidebar-accent))]',
      },
      size: {
        default: 'h-8 text-sm',
        sm: 'h-7 text-xs',
        lg: 'h-12 text-sm group-data-[collapsible=icon]:p-0!',
      },
    },
    defaultVariants: {
      variant: 'default',
      size: 'default',
    },
  },
);

const SidebarMenuButton = ({
  render,
  isActive = false,
  variant = 'default',
  size = 'default',
  tooltip,
  className,
  ...props
}: SidebarMenuButtonProps) => {
  const { isMobile, state } = useSidebar();

  const rendered = useRender({
    defaultTagName: 'button',
    props: mergeProps<'button'>(
      {
        className: cn(sidebarMenuButtonVariants({ variant, size }), className),
      },
      props,
    ),
    render: !tooltip ? render : <TooltipTrigger render={render} />,
    state: {
      slot: 'sidebar-menu-button',
      sidebar: 'menu-button',
      size,
      active: isActive,
    },
  });

  if (!tooltip) {
    return rendered;
  }

  const tooltipProps = typeof tooltip === 'string' ? { children: tooltip } : tooltip;

  return (
    <Tooltip>
      {rendered}
      <TooltipContent
        side="right"
        align="center"
        hidden={state !== 'collapsed' || isMobile}
        {...tooltipProps}
      />
    </Tooltip>
  );
};

export default SidebarMenuButton;
